import { useEffect, useState } from 'react';

import { ApiError, apiDownload, apiRequest } from '../lib/api-client';

type ReportPeriod = 'TODAY' | 'WEEK' | 'MONTH' | 'CUSTOM';
type PaymentMethod = 'CASH' | 'QRIS' | 'TRANSFER';

type FinancialReport = {
  generatedAt: string;
  outlet: { id: string; code: string; name: string };
  period: { type: ReportPeriod; from: string; to: string; label: string };
  today: { revenue: number; grossProfit: number; transactions: number; productsSold: number };
  month: { revenue: number; grossProfit: number; transactions: number };
  selected: { revenue: number; grossProfit: number; transactions: number; productsSold: number };
  paymentMethods: Array<{
    method: PaymentMethod;
    amount: number;
    transactions: number;
  }>;
  transactions: Array<{
    id: string;
    number: string;
    buyerName: string;
    source: 'READY_STOCK' | 'CUSTOM_ORDER';
    paymentMethod: PaymentMethod;
    total: number;
    grossProfit: number;
    createdAt: string;
    products: string;
    productsSold: number;
  }>;
};

const currency = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  maximumFractionDigits: 0,
});

const periodLabel: Record<ReportPeriod, string> = {
  TODAY: 'Hari Ini',
  WEEK: 'Minggu Ini',
  MONTH: 'Bulan Ini',
  CUSTOM: 'Rentang Tanggal',
};

const paymentLabel: Record<PaymentMethod, string> = {
  CASH: 'Tunai',
  QRIS: 'QRIS',
  TRANSFER: 'Transfer',
};

function today() {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Jakarta',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

export function FinancialReports({ outletReady }: { outletReady: boolean }) {
  const currentDate = today();
  const [period, setPeriod] = useState<ReportPeriod>('TODAY');
  const [from, setFrom] = useState(currentDate);
  const [to, setTo] = useState(currentDate);
  const [report, setReport] = useState<FinancialReport | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState<'pdf' | 'excel' | null>(null);
  const [error, setError] = useState('');

  function queryString() {
    const params = new URLSearchParams({ period });
    if (period === 'CUSTOM') {
      params.set('from', from);
      params.set('to', to);
    }
    return params.toString();
  }

  async function load() {
    if (!outletReady) return;
    if (period === 'CUSTOM' && (!from || !to || from > to)) {
      setError('Rentang tanggal belum valid.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      setReport(await apiRequest<FinancialReport>(`/reports/financial?${queryString()}`));
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Laporan keuangan gagal dimuat.');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
  }, [outletReady, period]);

  async function download(format: 'pdf' | 'excel') {
    if (period === 'CUSTOM' && (!from || !to || from > to)) {
      setError('Rentang tanggal belum valid.');
      return;
    }
    setExporting(format);
    setError('');
    try {
      const result = await apiDownload(`/reports/financial/${format}?${queryString()}`);
      const url = URL.createObjectURL(result.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.setTimeout(() => URL.revokeObjectURL(url), 1_000);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'File laporan gagal dibuat.');
    } finally {
      setExporting(null);
    }
  }

  if (!outletReady) {
    return (
      <div className="rounded-2xl bg-amber-50 p-6 text-amber-800">
        Belum ada outlet aktif. Pilih outlet untuk membuka laporan.
      </div>
    );
  }

  const todayCards = report
    ? [
        ['Omzet', currency.format(report.today.revenue)],
        ['Laba Kotor', currency.format(report.today.grossProfit)],
        ['Jumlah Transaksi', report.today.transactions.toLocaleString('id-ID')],
        ['Produk Terjual', report.today.productsSold.toLocaleString('id-ID')],
      ]
    : [];
  const monthCards = report
    ? [
        ['Total Omzet', currency.format(report.month.revenue)],
        ['Total Laba Kotor', currency.format(report.month.grossProfit)],
        ['Total Transaksi', report.month.transactions.toLocaleString('id-ID')],
      ]
    : [];

  return (
    <section className="page-shell">
      <div className="flex flex-col justify-between gap-4 lg:flex-row lg:items-end">
        <div>
          <p className="font-bold text-ajisai-700">Evaluasi Outlet</p>
          <h1 className="mt-1 text-3xl font-black tracking-tight">Laporan Keuangan</h1>
          <p className="mt-2 text-sm text-slate-500">
            Ringkasan otomatis dari Penjualan ready-stock dan Pesanan Custom yang sudah selesai.
          </p>
        </div>
        <div className="flex gap-2">
          <button
            className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-bold text-slate-700 disabled:opacity-50"
            disabled={!report || Boolean(exporting)}
            onClick={() => void download('pdf')}
          >
            {exporting === 'pdf' ? 'Membuat PDF...' : 'Export PDF'}
          </button>
          <button
            className="rounded-xl bg-ajisai-700 px-4 py-2.5 text-sm font-bold text-white transition hover:bg-ajisai-800 disabled:opacity-50"
            disabled={!report || Boolean(exporting)}
            onClick={() => void download('excel')}
          >
            {exporting === 'excel' ? 'Membuat Excel...' : 'Export Excel'}
          </button>
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap gap-2">
          {(Object.keys(periodLabel) as ReportPeriod[]).map((item) => (
            <button
              key={item}
              className={`rounded-xl px-4 py-2.5 text-sm font-bold ${
                period === item
                  ? 'bg-ajisai-700 text-white'
                  : 'border border-slate-300 text-slate-600'
              }`}
              onClick={() => setPeriod(item)}
            >
              {periodLabel[item]}
            </button>
          ))}
        </div>
        {period === 'CUSTOM' && (
          <form
            className="mt-4 grid gap-3 border-t border-slate-200 pt-4 sm:grid-cols-[1fr_1fr_auto]"
            onSubmit={(event) => {
              event.preventDefault();
              void load();
            }}
          >
            <label className="text-sm font-bold text-slate-700">
              Tanggal Awal
              <input
                className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 font-normal"
                type="date"
                max={to}
                required
                value={from}
                onChange={(event) => setFrom(event.target.value)}
              />
            </label>
            <label className="text-sm font-bold text-slate-700">
              Tanggal Akhir
              <input
                className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2.5 font-normal"
                type="date"
                min={from}
                max={currentDate}
                required
                value={to}
                onChange={(event) => setTo(event.target.value)}
              />
            </label>
            <button className="self-end rounded-xl bg-slate-900 px-5 py-2.5 font-bold text-white">
              Terapkan
            </button>
          </form>
        )}
      </div>

      {error && (
        <p role="alert" className="mt-5 rounded-xl bg-red-50 p-4 text-sm text-red-700">
          {error}
        </p>
      )}
      {loading && !report && (
        <p className="mt-6 rounded-2xl bg-white p-12 text-center text-sm text-slate-500">
          Memuat laporan...
        </p>
      )}

      {report && (
        <>
          <section className="mt-7">
            <div className="flex items-end justify-between gap-3">
              <div>
                <p className="text-sm font-bold text-ajisai-700">Dashboard Hari Ini</p>
                <h2 className="mt-1 text-xl font-black text-slate-900">Kinerja hari ini</h2>
              </div>
              <p className="text-xs text-slate-500">{report.outlet.name}</p>
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {todayCards.map(([label, value]) => (
                <article
                  key={label}
                  className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                >
                  <p className="text-sm font-semibold text-slate-500">{label}</p>
                  <p className="mt-5 break-words text-2xl font-black text-slate-950">{value}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="mt-8">
            <p className="text-sm font-bold text-ajisai-700">Dashboard Bulan Ini</p>
            <h2 className="mt-1 text-xl font-black text-slate-900">Kinerja bulan berjalan</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              {monthCards.map(([label, value]) => (
                <article
                  key={label}
                  className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                >
                  <p className="text-sm font-semibold text-slate-500">{label}</p>
                  <p className="mt-5 break-words text-2xl font-black text-slate-950">{value}</p>
                </article>
              ))}
            </div>
          </section>

          <section className="mt-8 rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <div className="flex flex-col justify-between gap-2 sm:flex-row sm:items-end">
              <div>
                <p className="text-sm font-bold text-ajisai-700">Periode Aktif</p>
                <h2 className="mt-1 text-xl font-black">{report.period.label}</h2>
              </div>
              <p className="text-sm text-slate-500">
                {report.period.from} s.d. {report.period.to}
              </p>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-xs text-slate-500">Omzet</p>
                <p className="mt-1 font-black">{currency.format(report.selected.revenue)}</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-xs text-slate-500">Laba Kotor</p>
                <p className="mt-1 font-black">{currency.format(report.selected.grossProfit)}</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-xs text-slate-500">Transaksi</p>
                <p className="mt-1 font-black">{report.selected.transactions}</p>
              </div>
              <div className="rounded-xl bg-slate-50 p-4">
                <p className="text-xs text-slate-500">Produk Terjual</p>
                <p className="mt-1 font-black">{report.selected.productsSold}</p>
              </div>
            </div>
          </section>

          <section className="mt-8">
            <p className="text-sm font-bold text-ajisai-700">Ringkasan Pembayaran</p>
            <h2 className="mt-1 text-xl font-black text-slate-900">Metode pembayaran</h2>
            <div className="mt-4 grid gap-4 sm:grid-cols-3">
              {report.paymentMethods.map((payment) => (
                <article
                  key={payment.method}
                  className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
                >
                  <p className="font-bold text-slate-600">{paymentLabel[payment.method]}</p>
                  <p className="mt-4 text-2xl font-black">{currency.format(payment.amount)}</p>
                  <p className="mt-2 text-xs text-slate-500">
                    {payment.transactions} pencatatan pembayaran
                  </p>
                </article>
              ))}
            </div>
          </section>

          <section className="mt-8 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
            <div className="border-b border-slate-200 p-5">
              <h2 className="font-black text-slate-900">Transaksi Periode Aktif</h2>
              <p className="mt-1 text-sm text-slate-500">
                Sumber data angka dan file export laporan.
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[760px] text-left text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-5 py-3">Tanggal</th>
                    <th className="px-5 py-3">Transaksi</th>
                    <th className="px-5 py-3">Produk/Pesanan</th>
                    <th className="px-5 py-3">Pembayaran</th>
                    <th className="px-5 py-3 text-right">Omzet</th>
                    <th className="px-5 py-3 text-right">Laba</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {report.transactions.map((transaction) => (
                    <tr key={transaction.id}>
                      <td className="whitespace-nowrap px-5 py-4 text-slate-500">
                        {new Date(transaction.createdAt).toLocaleString('id-ID')}
                      </td>
                      <td className="px-5 py-4">
                        <p className="font-bold">{transaction.buyerName}</p>
                        <p className="mt-1 text-xs text-slate-500">{transaction.number}</p>
                        <p className="mt-1 text-xs font-bold text-ajisai-700">
                          {transaction.source === 'CUSTOM_ORDER' ? 'Pesanan Custom' : 'Ready-stock'}
                        </p>
                      </td>
                      <td className="px-5 py-4">{transaction.products}</td>
                      <td className="px-5 py-4">{paymentLabel[transaction.paymentMethod]}</td>
                      <td className="px-5 py-4 text-right font-bold">
                        {currency.format(transaction.total)}
                      </td>
                      <td className="px-5 py-4 text-right font-bold text-emerald-700">
                        {currency.format(transaction.grossProfit)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {report.transactions.length === 0 && (
              <p className="px-5 py-12 text-center text-sm text-slate-500">
                Tidak ada transaksi pada periode ini.
              </p>
            )}
          </section>
        </>
      )}
    </section>
  );
}
